/**
 * Special Service — pure helpers behind the school order page's "Special
 * Service" dropdown and the "Supplies & Back Up Meals" dialog.
 *
 *   - `specialServiceOptions` decides which entries the dropdown offers (and
 *     therefore whether it renders at all).
 *   - The `backUp*` helpers turn the server's standing-order picture into an
 *     editable draft (per-item quantities + weekdays), step tiles against
 *     their caps, detect unsaved changes, and build the PUT body.
 *
 * Everything here is pure and unit-tested; the dialog only wires state.
 */

import type { BackUpStandingOrderWire, SaveBackUpStandingOrderInput, Weekday } from '@/api/school-back-up-meals'
import type { Mealtime } from '@/lib/mealtime'

export type SpecialServiceOption = 'FIELD_TRIP' | 'SPECIAL_DIETS' | 'BACKUP' | 'SUPPLIES'

/** Fresh backup uses BACKUP_MEALS; shelf-stable backup uses the existing BULK_SUPPLIES permission. Catalog and checkout enforce the same gates server-side. */
export function specialServiceOptions(school: {
  fieldTripMealsEnabled?: boolean
  specialDietsEnabled?: boolean
  backUpMealsEnabled?: boolean
  bulkSuppliesEnabled?: boolean
}): SpecialServiceOption[] {
  const options: SpecialServiceOption[] = []
  if (school.fieldTripMealsEnabled === true) {
    options.push('FIELD_TRIP')
  }
  if (school.specialDietsEnabled === true) {
    options.push('SPECIAL_DIETS')
  }
  if (school.backUpMealsEnabled === true || school.bulkSuppliesEnabled === true) {
    options.push('BACKUP')
  }
  if (school.bulkSuppliesEnabled === true) {
    options.push('SUPPLIES')
  }
  return options
}

/** Mon–Fri — what a campus that has never narrowed its cadence gets. */
export const DEFAULT_BACK_UP_WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5]

/** The weekday chip row, Monday first (school weeks). */
export const BACK_UP_WEEKDAY_CHIPS: ReadonlyArray<{ day: Weekday; label: string }> = [
  { day: 1, label: 'Mon' },
  { day: 2, label: 'Tue' },
  { day: 3, label: 'Wed' },
  { day: 4, label: 'Thu' },
  { day: 5, label: 'Fri' },
]

/** One mealtime's editable standing-order draft. */
export interface BackUpDraft {
  weekdays: Weekday[]
  /** Quantity per menuItemId; missing = 0. */
  quantities: Record<string, number>
}

/** The saved picture as a draft; a mealtime with no standing order starts Mon–Fri at zero. */
export function draftFromStandingOrder(order: Pick<BackUpStandingOrderWire, 'weekdays' | 'quantities'>): BackUpDraft {
  return {
    weekdays: order.weekdays.length > 0 ? sortWeekdays(order.weekdays) : [...DEFAULT_BACK_UP_WEEKDAYS],
    quantities: { ...order.quantities },
  }
}

/** Toggle one weekday; never lets the set go empty (a standing order with no days is meaningless). */
export function toggleBackUpWeekday(weekdays: readonly Weekday[], day: Weekday): Weekday[] {
  if (weekdays.includes(day)) {
    return weekdays.length === 1 ? [...weekdays] : weekdays.filter((d) => d !== day)
  }
  return sortWeekdays([...weekdays, day])
}

/**
 * Step one tile's quantity by `delta`, clamped to `[0, quantityCap]` (an
 * uncapped item has no ceiling). Returns the same object when nothing changes
 * so React state setters can short-circuit.
 */
export function stepBackUpQuantity(
  quantities: Readonly<Record<string, number>>,
  menuItemId: string,
  delta: number,
  quantityCap: number | null,
): Record<string, number> {
  const current = quantities[menuItemId] ?? 0
  const ceiling = typeof quantityCap === 'number' ? quantityCap : Number.POSITIVE_INFINITY
  const next = Math.max(0, Math.min(ceiling, current + delta))
  if (next === current) {
    return quantities as Record<string, number>
  }
  const out = { ...quantities }
  if (next === 0) {
    delete out[menuItemId]
  } else {
    out[menuItemId] = next
  }
  return out
}

/** Meals the standing order places per service day. */
export function backUpMealsPerDay(quantities: Readonly<Record<string, number>>): number {
  return Object.values(quantities).reduce((sum, q) => sum + q, 0)
}

/**
 * True when the draft differs from the saved picture. Weekdays only count
 * when the order has (or would have) meals — a zero draft is "no standing
 * order" regardless of which days are highlighted.
 */
export function hasBackUpChanges(
  saved: Pick<BackUpStandingOrderWire, 'weekdays' | 'quantities'>,
  draft: BackUpDraft,
): boolean {
  const savedTotal = backUpMealsPerDay(saved.quantities)
  const draftTotal = backUpMealsPerDay(draft.quantities)
  if (savedTotal === 0 && draftTotal === 0) {
    return false
  }
  if (!sameQuantities(saved.quantities, draft.quantities)) {
    return true
  }
  const savedDays = saved.weekdays.length > 0 ? sortWeekdays(saved.weekdays) : [...DEFAULT_BACK_UP_WEEKDAYS]
  return savedDays.join(',') !== sortWeekdays(draft.weekdays).join(',')
}

/** The PUT body for one mealtime. Zero rows are dropped; every tile is otherwise sent. */
export function buildBackUpSavePayload(
  schoolLocationId: string,
  order: Pick<BackUpStandingOrderWire, 'mealtime' | 'menuId' | 'items'>,
  draft: BackUpDraft,
): SaveBackUpStandingOrderInput {
  const live = new Set(order.items.map((item) => item.menuItemId))
  return {
    schoolLocationId,
    mealtime: order.mealtime,
    menuId: order.menuId,
    weekdays: sortWeekdays(draft.weekdays),
    items: Object.entries(draft.quantities)
      .filter(([menuItemId, quantity]) => quantity > 0 && live.has(menuItemId))
      .map(([menuItemId, quantity]) => ({ menuItemId, quantity })),
  }
}

/** Mealtimes with a published backup menu, in the wire's order, de-duplicated. */
export function backUpMealtimes(orders: ReadonlyArray<Pick<BackUpStandingOrderWire, 'mealtime'>>): Mealtime[] {
  return [...new Set(orders.map((o) => o.mealtime))]
}

function sortWeekdays(weekdays: readonly Weekday[]): Weekday[] {
  return [...new Set(weekdays)].sort((a, b) => a - b)
}

function sameQuantities(a: Readonly<Record<string, number>>, b: Readonly<Record<string, number>>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) {
    if ((a[key] ?? 0) !== (b[key] ?? 0)) {
      return false
    }
  }
  return true
}
